import React, { useEffect, useMemo, useRef, useState } from 'react';
import PhonePreview from '../components/onboarding/PhonePreview';
import CompareView from '../components/onboarding/CompareView';
import JsonEditorModal from '../components/onboarding/JsonEditorModal';
import { CATALOG, CATEGORIES, makePage, metaForField, defaultPages, REQUIRED_FIELDS, REQUIRED_SET } from '../onboarding/catalog';
import { t } from '../onboarding/strings';
import './OnboardingBuilder.css';

const STORAGE_KEY = 'kkal_onboarding_versions_v1';
const LEGACY_KEY = 'kkal_onboarding_draft_v1';
const EMPTY_ITEMS = [];

// Deterministic client-only item ids (regenerated each load; unique per session).
let UID = 1;
const nextUid = () => 'p' + UID++;
const wrap = (page, enabled = true) => ({ uid: nextUid(), page, enabled });
const clone = (o) => JSON.parse(JSON.stringify(o));
const newVid = () =>
  window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : 'v' + UID++ + '_' + nextUid();

function defaultItems() {
  return defaultPages.map((p) => wrap(clone(p), true));
}

// Minimal plan for a fresh version: the seven required pages, new_goal last.
function minimalItems() {
  return REQUIRED_FIELDS.map((f) => wrap(makePage(f), true));
}
function makeVersion(name, items, isDefault = false) {
  return { id: newVid(), name, isDefault, items };
}
function reWrap(items) {
  // Deep-clone + fresh uids so a duplicated version shares no references.
  return items.map((it) => wrap(clone(it.page), it.enabled));
}

// Load the version store, migrating an older single-draft if present.
function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.versions) && parsed.versions.length) {
        const versions = parsed.versions.map((v) => ({
          id: v.id || newVid(),
          name: v.name || 'Version',
          isDefault: !!v.isDefault,
          items: (v.items || []).map((it) => wrap(it.page, it.enabled !== false)),
        }));
        if (!versions.some((v) => v.isDefault)) versions[0].isDefault = true;
        const activeId = versions.some((v) => v.id === parsed.activeId) ? parsed.activeId : versions[0].id;
        return { versions, activeId };
      }
    }
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const arr = JSON.parse(legacy);
      if (Array.isArray(arr) && arr.length) {
        const v = makeVersion('Default', arr.map((it) => wrap(it.page, it.enabled !== false)), true);
        return { versions: [v], activeId: v.id };
      }
    }
  } catch (e) {
    /* ignore corrupt store */
  }
  const v = makeVersion('Default', defaultItems(), true);
  return { versions: [v], activeId: v.id };
}

export default function OnboardingBuilder() {
  const loaded = useRef(null);
  if (loaded.current === null) loaded.current = loadStore();

  const [versions, setVersions] = useState(() => loaded.current.versions);
  const [activeId, setActiveId] = useState(() => loaded.current.activeId);
  const [selectedUid, setSelectedUid] = useState(() => {
    const a = loaded.current.versions.find((v) => v.id === loaded.current.activeId);
    return a && a.items[0] ? a.items[0].uid : null;
  });
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [modal, setModal] = useState(null); // 'import' | 'export' | null
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [copied, setCopied] = useState(false);
  const [vMenu, setVMenu] = useState(false);
  const dragRef = useRef(null);

  const active = versions.find((v) => v.id === activeId) || versions[0];
  const items = useMemo(() => (active ? active.items : EMPTY_ITEMS), [active]);

  // Write helper that targets the active version (supports updater fn or array).
  const setItems = (updater) =>
    setVersions((vs) =>
      vs.map((v) =>
        v.id === activeId
          ? { ...v, items: typeof updater === 'function' ? updater(v.items) : updater }
          : v
      )
    );

  // Autosave the whole store.
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          activeId,
          versions: versions.map((v) => ({
            id: v.id,
            name: v.name,
            isDefault: v.isDefault,
            items: v.items.map((it) => ({ page: it.page, enabled: it.enabled })),
          })),
        })
      );
    } catch (e) {
      /* quota / private mode — non-fatal */
    }
  }, [versions, activeId]);

  const enabledItems = useMemo(() => items.filter((i) => i.enabled), [items]);
  const selected = items.find((i) => i.uid === selectedUid) || null;
  const previewIndex = selected
    ? Math.max(0, enabledItems.findIndex((i) => i.uid === selected.uid))
    : 0;

  const exportArray = useMemo(() => enabledItems.map((i) => i.page), [enabledItems]);
  const exportJson = useMemo(() => JSON.stringify(exportArray, null, 2), [exportArray]);
  const warnings = useMemo(() => computeWarnings(enabledItems), [enabledItems]);

  /* ---- version operations ---- */
  function selectFirst(version) {
    setSelectedUid(version && version.items[0] ? version.items[0].uid : null);
  }
  function switchVersion(id) {
    setActiveId(id);
    selectFirst(versions.find((v) => v.id === id));
    setDragOverIndex(null);
  }
  function addVersion(name, newItems, makeDefaultFlag = false) {
    const v = makeVersion(name, newItems, makeDefaultFlag);
    setVersions((vs) =>
      (makeDefaultFlag ? vs.map((x) => ({ ...x, isDefault: false })) : vs).concat(v)
    );
    setActiveId(v.id);
    selectFirst(v);
    setVMenu(false);
  }
  function createVersion(kind) {
    const suggested =
      kind === 'duplicate' ? `${active.name} copy`
      : kind === 'default' ? 'New (default)'
      : kind === 'blank' ? 'New (minimal)'
      : 'New version';
    const name = window.prompt('Name for the new version:', suggested);
    if (name === null) { setVMenu(false); return; }
    const finalName = name.trim() || suggested;
    if (kind === 'duplicate') addVersion(finalName, reWrap(active.items));
    else if (kind === 'default') addVersion(finalName, defaultItems());
    else addVersion(finalName, minimalItems());
  }
  function renameVersion(id) {
    const v = versions.find((x) => x.id === id);
    const name = window.prompt('Rename version:', v ? v.name : '');
    if (name === null) return;
    const finalName = name.trim();
    if (!finalName) return;
    setVersions((vs) => vs.map((x) => (x.id === id ? { ...x, name: finalName } : x)));
  }
  function setDefaultVersion(id) {
    setVersions((vs) => vs.map((x) => ({ ...x, isDefault: x.id === id })));
  }
  function deleteVersion(id) {
    if (versions.length <= 1) { window.alert('At least one version is required.'); return; }
    const v = versions.find((x) => x.id === id);
    if (!window.confirm(`Delete version "${v ? v.name : ''}"? This cannot be undone.`)) return;
    setVersions((vs) => {
      const next = vs.filter((x) => x.id !== id);
      if (!next.some((x) => x.isDefault)) next[0] = { ...next[0], isDefault: true };
      if (id === activeId) {
        setActiveId(next[0].id);
        selectFirst(next[0]);
      }
      return next;
    });
  }

  /* ---- item mutations (operate on active version) ---- */
  function addPage(field, atIndex) {
    const page = makePage(field);
    if (!page) return;
    const item = wrap(page, true);
    setItems((prev) => {
      const next = prev.slice();
      next.splice(atIndex == null ? next.length : atIndex, 0, item);
      return next;
    });
    setSelectedUid(item.uid);
  }
  // A required page is locked when it's the only instance of its field.
  function isLockedRow(it) {
    if (!REQUIRED_SET.has(it.page.field)) return false;
    return items.filter((x) => x.page.field === it.page.field).length <= 1;
  }

  function removePage(uid) {
    const it = items.find((i) => i.uid === uid);
    if (it && isLockedRow(it)) {
      window.alert(`"${it.page.field}" is a required page — it can be moved, but not deleted.`);
      return;
    }
    setItems((prev) => prev.filter((i) => i.uid !== uid));
    if (selectedUid === uid) setSelectedUid(null);
  }
  function duplicatePage(uid) {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.uid === uid);
      if (idx < 0) return prev;
      const copy = wrap(clone(prev[idx].page), prev[idx].enabled);
      const next = prev.slice();
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }
  function toggleEnabled(uid) {
    const it = items.find((i) => i.uid === uid);
    if (it && it.enabled && REQUIRED_SET.has(it.page.field)) {
      const enabledCount = items.filter((x) => x.enabled && x.page.field === it.page.field).length;
      if (enabledCount <= 1) {
        window.alert(`"${it.page.field}" is a required page — it can't be excluded from the onboarding.`);
        return;
      }
    }
    setItems((prev) => prev.map((i) => (i.uid === uid ? { ...i, enabled: !i.enabled } : i)));
  }
  function resetToDefault() {
    if (!window.confirm(`Replace "${active.name}" with the bundled default (30 pages)?`)) return;
    const fresh = defaultItems();
    setItems(fresh);
    setSelectedUid(fresh[0] ? fresh[0].uid : null);
  }

  /* ---- drag & drop ---- */
  function onRowDragStart(uid, e) {
    dragRef.current = { kind: 'move', uid };
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', uid); } catch (_) {}
  }
  function onPaletteDragStart(field, e) {
    dragRef.current = { kind: 'new', field };
    e.dataTransfer.effectAllowed = 'copy';
    try { e.dataTransfer.setData('text/plain', field); } catch (_) {}
  }
  function onDragOverAt(index, e) {
    e.preventDefault();
    if (dragOverIndex !== index) setDragOverIndex(index);
  }
  function performDrop(targetIndex) {
    const payload = dragRef.current;
    dragRef.current = null;
    setDragOverIndex(null);
    if (!payload) return;
    if (payload.kind === 'new') { addPage(payload.field, targetIndex); return; }
    setItems((prev) => {
      const from = prev.findIndex((i) => i.uid === payload.uid);
      if (from < 0) return prev;
      const next = prev.slice();
      const [moved] = next.splice(from, 1);
      let to = targetIndex;
      if (from < targetIndex) to -= 1;
      to = Math.max(0, Math.min(to, next.length));
      next.splice(to, 0, moved);
      return next;
    });
    setSelectedUid(payload.uid);
  }
  function onListDrop(e) {
    e.preventDefault();
    performDrop(dragOverIndex == null ? items.length : dragOverIndex);
  }

  /* ---- import / export ---- */
  function parseImport() {
    setImportError('');
    let parsed;
    try { parsed = JSON.parse(importText); } catch (e) { setImportError('Invalid JSON: ' + e.message); return null; }
    if (!Array.isArray(parsed)) { setImportError('Expected a JSON array of pages.'); return null; }
    const bad = parsed.findIndex((p) => !p || !p.field || !p.content || !p.content.type);
    if (bad >= 0) { setImportError(`Item ${bad} is missing field / content.type.`); return null; }
    return parsed;
  }
  function importAsNew() {
    const parsed = parseImport();
    if (!parsed) return;
    const name = window.prompt('Name for the imported version:', 'Imported');
    if (name === null) return;
    addVersion(name.trim() || 'Imported', parsed.map((p) => wrap(p, true)));
    setModal(null);
    setImportText('');
  }
  function importReplace() {
    const parsed = parseImport();
    if (!parsed) return;
    const next = parsed.map((p) => wrap(p, true));
    setItems(next);
    setSelectedUid(next[0] ? next[0].uid : null);
    setModal(null);
    setImportText('');
  }
  function copyJson() {
    navigator.clipboard.writeText(exportJson).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => setCopied(false)
    );
  }
  function downloadJson() {
    const safe = active.name.replace(/[^a-z0-9_-]+/gi, '_').toLowerCase();
    const blob = new Blob([exportJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding_${safe || 'version'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ---- raw JSON editor (active version) ---- */
  function applyJsonBlocks(entries) {
    const next = entries.map(({ page, enabled }) => wrap(page, enabled));
    setItems(next);
    setSelectedUid(next[0] ? next[0].uid : null);
    setModal(null);
  }

  return (
    <div className="ob">
      <div className="ob-topbar">
        <div>
          <h1 className="ob-h1">🧩 Onboarding Constructor</h1>
          <div className="ob-sub">
            Build onboarding versions, preview them, and export JSON for Firebase Remote Config key{' '}
            <code>onboarding_setup_pages_json</code>.
          </div>
        </div>
        <div className="ob-actions">
          <span className="ob-count">{enabledItems.length} active · {items.length} total</span>
          <button className="btn btn-secondary" onClick={() => setModal('compare')}>⇄ Compare</button>
          <button className="btn btn-secondary" onClick={resetToDefault}>↺ Reset to default</button>
          <button className="btn btn-secondary" onClick={() => { setModal('import'); setImportError(''); }}>⇣ Import</button>
          <button className="btn btn-secondary" onClick={() => setModal('json')}>{'{ }'} JSON</button>
          <button className="btn btn-primary" onClick={() => setModal('export')}>⤓ Export JSON</button>
        </div>
      </div>

      {/* Version bar */}
      <div className="ob-versions">
        <span className="ob-versions-label">Versions</span>
        {versions.map((v) => (
          <div
            key={v.id}
            className={`ob-vtab ${v.id === activeId ? 'active' : ''}`}
            onClick={() => switchVersion(v.id)}
            onDoubleClick={() => renameVersion(v.id)}
            title="Click to edit · double-click to rename"
          >
            <button
              className="ob-vstar"
              title={v.isDefault ? 'Default version' : 'Set as default'}
              onClick={(e) => { e.stopPropagation(); setDefaultVersion(v.id); }}
            >
              {v.isDefault ? '★' : '☆'}
            </button>
            <span className="ob-vname">{v.name}</span>
            <span className="ob-vcount">{v.items.filter((i) => i.enabled).length}</span>
            <button className="ob-vedit" title="Rename version" onClick={(e) => { e.stopPropagation(); renameVersion(v.id); }}>✎</button>
            {versions.length > 1 && (
              <button className="ob-vdel" title="Delete version" onClick={(e) => { e.stopPropagation(); deleteVersion(v.id); }}>✕</button>
            )}
          </div>
        ))}
        <div className="ob-vnew-wrap">
          <button className="ob-vnew" onClick={() => setVMenu((s) => !s)}>＋ New version ▾</button>
          {vMenu && (
            <>
              <div className="ob-vmenu-backdrop" onClick={() => setVMenu(false)} />
              <div className="ob-vmenu">
                <button onClick={() => createVersion('default')}>📋 From iOS default (30 pages)</button>
                <button onClick={() => createVersion('duplicate')}>⧉ Duplicate “{active.name}”</button>
                <button onClick={() => createVersion('blank')}>➕ Minimal (7 required pages)</button>
                <button onClick={() => { setVMenu(false); setModal('import'); setImportError(''); }}>⇣ From JSON…</button>
              </div>
            </>
          )}
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="ob-warnings">
          {warnings.map((w, i) => (<div key={i} className="ob-warning">⚠ {w}</div>))}
        </div>
      )}

      <div className="ob-main">
        {/* Palette */}
        <aside className="ob-palette">
          <div className="ob-palette-title">Add a page</div>
          {CATEGORIES.map((cat) => {
            const entries = CATALOG.filter((c) => c.category === cat);
            if (!entries.length) return null;
            return (
              <div key={cat} className="ob-cat">
                <div className="ob-cat-name">{cat}</div>
                {entries.map((c) => (
                  <div
                    key={c.id}
                    className="ob-palette-item"
                    draggable
                    onDragStart={(e) => onPaletteDragStart(c.field, e)}
                    onClick={() => addPage(c.field)}
                    title={`Add "${c.label}"`}
                  >
                    <span className="ob-pi-emoji">{c.emoji}</span>
                    <span className="ob-pi-label">{c.label}</span>
                    <span className="ob-pi-add">＋</span>
                  </div>
                ))}
              </div>
            );
          })}
        </aside>

        {/* Sequence */}
        <section
          className="ob-sequence"
          onDragOver={(e) => { e.preventDefault(); if (dragOverIndex == null) setDragOverIndex(items.length); }}
          onDrop={onListDrop}
        >
          {items.length === 0 && (<div className="ob-empty">No pages in “{active.name}”. Add from the left or Import JSON.</div>)}
          {items.map((it, idx) => {
            const meta = metaForField(it.page.field);
            const liveIndex = enabledItems.findIndex((e) => e.uid === it.uid);
            return (
              <React.Fragment key={it.uid}>
                {dragOverIndex === idx && <div className="ob-drop-line" />}
                <div
                  className={`ob-row ${selectedUid === it.uid ? 'is-selected' : ''} ${it.enabled ? '' : 'is-disabled'}`}
                  draggable
                  onDragStart={(e) => onRowDragStart(it.uid, e)}
                  onDragOver={(e) => onDragOverAt(idx, e)}
                  onDrop={onListDrop}
                  onClick={() => setSelectedUid(it.uid)}
                >
                  <span className="ob-handle" title="Drag to reorder">⠿</span>
                  <span className="ob-idx">{it.enabled && liveIndex >= 0 ? liveIndex + 1 : '–'}</span>
                  <span className="ob-row-emoji">{meta.emoji}</span>
                  <span className="ob-row-main">
                    <span className="ob-row-title">{t(it.page.title) || meta.label}</span>
                    <span className="ob-row-type">{it.page.content && it.page.content.type}</span>
                  </span>
                  <button className={`ob-mini ${it.enabled ? 'on' : ''}`} title={it.enabled ? 'Enabled — click to exclude' : 'Disabled'} onClick={(e) => { e.stopPropagation(); toggleEnabled(it.uid); }}>{it.enabled ? '👁' : '🚫'}</button>
                  <button className="ob-mini" title="Duplicate" onClick={(e) => { e.stopPropagation(); duplicatePage(it.uid); }}>⧉</button>
                  {isLockedRow(it) ? (
                    <span className="ob-mini ob-lock" title="Required page — move it, but it can't be deleted">🔒</span>
                  ) : (
                    <button className="ob-mini danger" title="Delete" onClick={(e) => { e.stopPropagation(); removePage(it.uid); }}>✕</button>
                  )}
                </div>
              </React.Fragment>
            );
          })}
          {dragOverIndex === items.length && <div className="ob-drop-line" />}
          <div className="ob-end-zone" onDragOver={(e) => onDragOverAt(items.length, e)} onDrop={onListDrop}>
            Drop here to add to the end
          </div>
        </section>

        {/* Preview */}
        <aside className="ob-preview">
          <PhonePreview page={selected ? selected.page : null} index={previewIndex} total={enabledItems.length} />
          {selected && (
            <div className="ob-preview-meta">
              <div><b>version</b> <code>{active.name}</code></div>
              <div><b>field</b> <code>{selected.page.field}</code></div>
              <div><b>type</b> <code>{selected.page.content && selected.page.content.type}</code></div>
              <div><b>title key</b> <code>{selected.page.title}</code></div>
            </div>
          )}
        </aside>
      </div>

      {/* Compare overlay */}
      {modal === 'compare' && (
        <CompareView
          versions={versions}
          onClose={() => setModal(null)}
          initialA={activeId}
          initialB={(versions.find((v) => v.id !== activeId) || {}).id}
        />
      )}

      {/* Import modal */}
      {modal === 'import' && (
        <Modal title="Import onboarding JSON" onClose={() => setModal(null)}>
          <p className="ob-modal-hint">
            Paste a pages array (e.g. the current Remote Config <code>onboarding_setup_pages_json</code>) to
            create a new version, or replace the current one.
          </p>
          <textarea className="ob-textarea" value={importText} onChange={(e) => setImportText(e.target.value)} placeholder='[ { "title": "...", "field": "...", "content": { "type": "..." } } ]' />
          {importError && <div className="ob-modal-error">{importError}</div>}
          <div className="ob-modal-actions">
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-secondary" onClick={importReplace}>Replace “{active.name}”</button>
            <button className="btn btn-primary" onClick={importAsNew}>Create new version</button>
          </div>
        </Modal>
      )}

      {/* Raw JSON editor (per-page collapsible blocks, syntax-highlighted) */}
      {modal === 'json' && (
        <JsonEditorModal
          versionName={active.name}
          items={items}
          onApply={applyJsonBlocks}
          onClose={() => setModal(null)}
        />
      )}

      {/* Export modal */}
      {modal === 'export' && (
        <Modal title={`Export “${active.name}”`} onClose={() => setModal(null)}>
          <p className="ob-modal-hint">
            {enabledItems.length} active page(s). Paste into Firebase Console → Remote Config →{' '}
            <code>onboarding_setup_pages_json</code> (stored as a string).
          </p>
          {warnings.length > 0 && (<div className="ob-modal-error">{warnings.length} warning(s) — export still allowed. Review them above.</div>)}
          <textarea className="ob-textarea" readOnly value={exportJson} onFocus={(e) => e.target.select()} />
          <div className="ob-modal-actions">
            <button className="btn btn-secondary" onClick={downloadJson}>⤓ Download</button>
            <button className="btn btn-primary" onClick={copyJson}>{copied ? '✓ Copied' : '⧉ Copy JSON'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="ob-modal-overlay" onClick={onClose}>
      <div className="ob-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ob-modal-head">
          <h3>{title}</h3>
          <button className="ob-modal-x" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function computeWarnings(enabledItems) {
  const warnings = [];
  if (enabledItems.length === 0) {
    warnings.push('No active pages — this version would be empty.');
    return warnings;
  }
  const missingRequired = REQUIRED_FIELDS.filter(
    (f) => !enabledItems.some((i) => i.page.field === f)
  );
  if (missingRequired.length) {
    warnings.push(
      `Required pages missing or disabled: ${missingRequired.join(', ')} — every onboarding needs ` +
      REQUIRED_FIELDS.join(', ') + '.'
    );
  }
  const idxOf = (f) => enabledItems.findIndex((i) => i.page.field === f);
  const newGoalIdx = idxOf('new_goal');
  if (newGoalIdx >= 0) {
    const after = REQUIRED_FIELDS.filter((f) => f !== 'new_goal' && idxOf(f) > newGoalIdx);
    if (after.length) {
      warnings.push(
        `"new_goal" (plan calculation) must be the last of the required pages — move it below: ${after.join(', ')}.`
      );
    }
  }
  if (!enabledItems.some((i) => i.page.field === 'name')) {
    warnings.push('No "name" page — iOS logs a warning when the name page is missing.');
  }
  const counts = {};
  enabledItems.forEach((i) => { counts[i.page.field] = (counts[i.page.field] || 0) + 1; });
  Object.entries(counts).forEach(([field, n]) => {
    if (n > 1 && !metaForField(field).repeatable) {
      warnings.push(`"${field}" appears ${n}× — usually it should be unique.`);
    }
  });
  enabledItems.forEach((i, idx) => {
    if (i.page.content && i.page.content.type === 'input_text' && i.page.field !== 'promo_code') {
      warnings.push(`Page ${idx + 1}: input_text only renders in-app when field is "promo_code".`);
    }
  });
  return warnings;
}
